import SocialLinks from "@/components/shared/SocialLinks";
import { AftershootPlanStatus } from "@/data/constants";
import useAppNavigate from "@/hooks/useAppNavigate";
import useLogoutHandler from "@/hooks/useLogoutHandler";
import { TUserProfileResponse } from "@/lib/user/fetchUserProfile";
import { TUserPlanData } from "@/types/plan-data";
import { getTwoLetterOrInitials } from "@/utils/initials";
import { useQueryClient } from "@tanstack/react-query";
import { differenceInCalendarDays } from "date-fns";
import { motion, Variants } from "motion/react";
import { PhSignOut, VscChromeClose } from "@pkg/icons";
import { useEffect } from "react";
import { useUser } from "reactfire";
import GradientBar from "../components/GradientBar";
import { mobileNavItems } from "../config.nav";
import { NavLinkItem } from "./NavLinkItem";
import { NavLinks } from "./NavLinks";
import { NavLogo } from "./NavLogo";

const variants: Variants = {
  closed: { width: "0", opacity: 0 },
  opened: { width: "100%", opacity: 1 },
};

interface NavMobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  /** Photon funnel context: hides the product nav tabs, leaving only Log out */
  isPhotonFlow?: boolean;
}

/** Animated fullscreen overlay for mobile navigation — pure container, no business logic. */
export const NavMobileMenu: React.FC<NavMobileMenuProps> = ({
  isOpen,
  onClose,
  children,
  isPhotonFlow = false,
}) => {
  const navigateTo = useAppNavigate();
  const { handleLogout } = useLogoutHandler();

  const { data } = useUser();

  const client = useQueryClient();
  const userQuery = client.getQueryData<TUserProfileResponse>(["user"]);
  const planData = client.getQueryData<TUserPlanData>(["user-plan-data"]);

  const handleLogoutClick = () => {
    handleLogout();
    onClose();
  };
  // Close on md+ resize
  useEffect(() => {
    if (!isOpen) return;
    const mql = window.matchMedia("(min-width: 640px)");
    const handleChange = (e: MediaQueryListEvent) => {
      if (e.matches) onClose();
    };
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, [isOpen, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <motion.div
      data-mobile-menu
      variants={variants}
      initial="closed"
      animate={isOpen ? "opened" : "closed"}
      transition={{ opacity: { duration: 0.1 }, default: { ease: "backOut" } }}
      className={`dark fixed inset-0 z-100 flex h-dvh w-full flex-col bg-black lg:hidden ${
        isOpen ? "" : "pointer-events-none"
      }`}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-black">
        <div className="flex shrink-0 items-center justify-between px-4 py-4 text-white">
          <NavLogo dark />
          <VscChromeClose
            className="h-5 w-5 cursor-pointer text-white"
            onClick={onClose}
          />
        </div>

        <div className="shrink-0 p-4">
          {data && (
            <div className="flex flex-col items-center justify-between space-y-1 pb-8 text-white">
              {data?.photoURL ? (
                <img
                  src={data.photoURL}
                  alt="Profile Picture"
                  className="h-12 w-12 rounded-full border-2 border-cornflowerBlue object-cover"
                ></img>
              ) : (
                <div className="flex h-12 w-12 items-center justify-center truncate rounded-full border-2 border-cornflowerBlue bg-[#e53935] text-xl uppercase">
                  {getTwoLetterOrInitials(data?.displayName || "")}
                </div>
              )}

              {/* copy */}
              <div className="max-w-[60%] truncate font-erode-variable text-lg font-light tracking-[-0.3]">
                {data?.displayName}
              </div>

              <div className="text-xxs font-extralight uppercase">
                <div className="rounded-sm bg-rockSpray px-2 py-1">
                  {planData?.planName}
                </div>
                {userQuery &&
                  userQuery.data.status === AftershootPlanStatus.TRIALING &&
                  userQuery.data.trialEnd !== null && (
                    <>
                      {differenceInCalendarDays(
                        new Date(),
                        new Date(userQuery.data.trialEnd),
                      )}{" "}
                      DAYS left
                    </>
                  )}
              </div>
            </div>
          )}

          <NavLinks>
            {!isPhotonFlow &&
              mobileNavItems.map((item) => (
                <NavLinkItem
                  key={item.title}
                  href={item.external ? item.url : undefined}
                  external={item.external}
                  onClick={
                    !item.external
                      ? () => {
                          navigateTo(item.url);
                          onClose();
                        }
                      : undefined
                  }
                  className="font-erode-variable text-xl font-light"
                >
                  {item.title}
                </NavLinkItem>
              ))}
          </NavLinks>

          {data && (
            <div className="flex justify-center pt-8">
              <button
                onClick={handleLogoutClick}
                className="flex items-center gap-2 px-6 py-3 font-light"
              >
                <PhSignOut size={24} />
                Log out
              </button>
            </div>
          )}
        </div>

        {children}

        <SocialLinks
          className="mx-auto w-max shrink-0 pb-16"
          iconClassName="h-4 w-4"
        />
      </div>
    </motion.div>
  );
};
